import { RECOVER_LOCAL_AUDIO_DEBOUNCE_MS } from './constants';
import { republishPublisherAudio } from './publisher';
import type { JanusCallRefs, JanusCallSetters } from './sessionRefs';
import { logJanus } from './utils';
import { acquireBrowserDefaultMicStream } from './webDevices';

async function attachMicTrackToPublisher(
  pc: RTCPeerConnection,
  track: MediaStreamTrack,
  stream: MediaStream
): Promise<void> {
  const senderWithLiveAudio = pc.getSenders().find((s) => s.track?.kind === 'audio');
  if (senderWithLiveAudio) {
    await senderWithLiveAudio.replaceTrack(track);
    return;
  }

  const audioTransceiver = pc.getTransceivers().find(
    (t) =>
      t.sender.track?.kind === 'audio' ||
      t.receiver.track?.kind === 'audio' ||
      (t.sender.track == null && (t.direction === 'recvonly' || t.direction === 'inactive'))
  );

  if (audioTransceiver) {
    audioTransceiver.direction = 'sendrecv';
    await audioTransceiver.sender.replaceTrack(track);
    return;
  }

  pc.addTrack(track, stream);
}

export async function recoverLocalAudioImpl(
  r: JanusCallRefs,
  set: JanusCallSetters,
  bindMicTrackLifecycle: (track: MediaStreamTrack) => void,
  sendPublisherMessage: (
    body: Record<string, unknown>,
    jsep?: RTCSessionDescriptionInit,
    transaction?: string
  ) => void
): Promise<boolean> {
  const pc = r.publisherPcRef.current;
  if (!pc || pc.signalingState === 'closed') {
    return false;
  }
  if (!r.isJoinedRef.current && r.callStatusRef.current !== 'oncall') {
    return false;
  }

  try {
    const stream = await acquireBrowserDefaultMicStream();
    const [newTrack] = stream.getAudioTracks();
    if (!newTrack) {
      stream.getTracks().forEach((tr) => tr.stop());
      return false;
    }

    bindMicTrackLifecycle(newTrack);
    r.isMutedRef.current = false;
    newTrack.enabled = true;

    await attachMicTrackToPublisher(pc, newTrack, stream);

    r.localStreamRef.current?.getTracks().forEach((tr) => tr.stop());
    set.setLocalStream(stream);
    r.localStreamRef.current = stream;
    r.micDisconnectHandledRef.current = false;

    try {
      await republishPublisherAudio(r, sendPublisherMessage);
    } catch (err) {
      logJanus('MIC', 'republish after recover failed', err);
    }

    r.onLocalAudioRecoveredRef.current?.();
    return true;
  } catch (err) {
    logJanus('MIC', 'recoverLocalAudio failed', err);
    r.micDisconnectHandledRef.current = true;
    return false;
  }
}
